// TelcoTrack — paginated fetch-all helper for materials-used API.

import { forkJoin, Observable, of } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';
import {
  ObjectService,
  type MaterialsUsedPageItem,
  type MaterialsUsedPageResponse,
} from '../../core/services/object.service';
import type { MaterialPanelRow } from './materials-panel.types';
import { mapMaterialsPageItem } from './materials-display.util';

export const MATERIALS_FETCH_PAGE_SIZE = 500;

export interface MaterialsFetchParams {
  months?: number;
  month?: string;
  dateFrom?: string;
  dateTo?: string;
  changedBy?: string;
  polygonId?: string;
  mainCategory?: string;
  objectType?: string;
  objectStatus?: string;
  activePolygonOnly?: boolean;
}

export function fetchAllMaterialsPages(
  objectService: ObjectService,
  params: MaterialsFetchParams,
): Observable<MaterialPanelRow[]> {
  const base = { ...params, limit: MATERIALS_FETCH_PAGE_SIZE, offset: 0 };

  const loadPage = (offset: number) =>
    objectService.getMaterialsUsed({
      ...base,
      offset,
    });

  return loadPage(0).pipe(
    switchMap((first) => {
      const total = first.totalCount;
      const pages = Math.max(1, Math.ceil(total / MATERIALS_FETCH_PAGE_SIZE));
      if (pages <= 1) {
        return of(first.items.map((it) => mapMaterialsPageItem(it)));
      }
      const rest: Observable<MaterialsUsedPageResponse>[] = [];
      for (let i = 1; i < pages; i++) {
        rest.push(
          loadPage(i * MATERIALS_FETCH_PAGE_SIZE).pipe(
            catchError(() => of({ items: [], totalCount: 0, aggregateLineTotal: 0 } as MaterialsUsedPageResponse)),
          ),
        );
      }
      return forkJoin(rest).pipe(
        map((chunks) => {
          const all: MaterialsUsedPageItem[] = [...first.items];
          for (const c of chunks) all.push(...c.items);
          return all.map((it) => mapMaterialsPageItem(it));
        }),
      );
    }),
  );
}
